import {
  useCallback,
  useId,
  useRef,
  useState,
  type ComponentType,
  type KeyboardEvent,
  type ReactNode,
  type Ref,
} from 'react';
import { DotsThree } from '@phosphor-icons/react';
import type { IconProps } from '@phosphor-icons/react';
import { cx } from '../cx';
import { Icon } from '../Icon';
import { IconButton } from '../IconButton/IconButton';
import { Portal } from '../Portal';
import { useDismiss } from '../hooks/useDismiss';
import { usePosition, type Placement } from '../hooks/usePosition';
import { devWarn } from '../dev';
import { Sheet } from '../Sheet/Sheet';

export type MenuItem = {
  id: string;
  label: ReactNode;
  /** Binoculars for follow/watch, the bell only for alerts. */
  icon?: ComponentType<IconProps>;
  onSelect: () => void;
  /** Destructive: drawn last, after a separator, in the negative colour. */
  danger?: boolean;
  disabled?: boolean;
};

const itemClass = (danger?: boolean) =>
  cx(
    'flex min-h-10 w-full items-center gap-3 rounded-jc-info px-3 text-left text-sm outline-none',
    'transition-colors duration-(--jc-duration-fast) hover:bg-jc-elevated focus-visible:bg-jc-elevated',
    'disabled:cursor-not-allowed disabled:opacity-50',
    danger ? 'text-jc-negative' : 'text-jc-ink',
  );

export type MenuPanelProps = {
  items: MenuItem[];
  id?: string;
  labelledBy?: string;
  onSelect?: (item: MenuItem) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLDivElement>) => void;
  itemRef?: (index: number, el: HTMLButtonElement | null) => void;
  className?: string;
};

/** The menu surface on its own (used by Menu, and by the showcase to draw it open). Danger items go last. */
export function MenuPanel({ items, id, labelledBy, onSelect, onKeyDown, itemRef, className }: MenuPanelProps) {
  const regular = items.filter((i) => !i.danger);
  const danger = items.filter((i) => i.danger);
  const ordered = [...regular, ...danger];
  return (
    <div
      id={id}
      role="menu"
      aria-labelledby={labelledBy}
      onKeyDown={onKeyDown}
      className={cx(
        'flex min-w-56 flex-col rounded-jc-container bg-jc-surface p-1.5 shadow-jc-lg ring-1 ring-jc-card-border',
        className,
      )}
    >
      {ordered.map((item, index) => (
        <div key={item.id} className="contents">
          {danger.length > 0 && index === regular.length && regular.length > 0 ? (
            <div role="separator" className="my-1 h-px bg-jc-border" />
          ) : null}
          <button
            ref={(el) => itemRef?.(index, el)}
            type="button"
            role="menuitem"
            tabIndex={-1}
            disabled={item.disabled}
            onClick={() => onSelect?.(item)}
            className={itemClass(item.danger)}
          >
            {item.icon ? <Icon icon={item.icon} size="md" /> : null}
            <span className="truncate">{item.label}</span>
          </button>
        </div>
      ))}
    </div>
  );
}

export type MenuTriggerProps = {
  ref: Ref<HTMLButtonElement>;
  'aria-haspopup': 'menu';
  'aria-expanded': boolean;
  'aria-controls': string | undefined;
  onClick: () => void;
  onKeyDown: (e: KeyboardEvent<HTMLButtonElement>) => void;
};

export type MenuProps = {
  /** At most seven. */
  items: MenuItem[];
  /** Accessible name of the trigger, localised by the caller ("More actions for this job"); also the action sheet's title on phones. */
  label: string;
  /** Accessible name of the action sheet's close button on phones, localised. */
  closeLabel: string;
  icon?: ComponentType<IconProps>;
  placement?: Placement;
  /** A custom trigger; spread the props onto a button. Defaults to a ghost IconButton. */
  trigger?: (props: MenuTriggerProps) => ReactNode;
};

const PHONE_QUERY = '(max-width: 767px)';
export const MENU_MAX_ITEMS = 7;

/**
 * A short list of actions (at most seven) behind an overflow button: a popover from md, an action
 * sheet on phones. role="menu": opening focuses the first item, arrows move, Home/End jump, Escape
 * or Tab closes and focus returns to the trigger.
 */
export function Menu({ items, label, closeLabel, icon = DotsThree, placement = 'bottom-end', trigger }: MenuProps) {
  devWarn(items.length > MENU_MAX_ITEMS, `Menu "${label}" has ${items.length} items; at most ${MENU_MAX_ITEMS}.`);
  const [open, setOpen] = useState(false);
  const [asSheet, setAsSheet] = useState(false);
  const menuId = `mn${useId().replace(/:/g, '')}`;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const firstItemRef = useRef<HTMLButtonElement | null>(null);
  const itemEls = useRef<Array<HTMLButtonElement | null>>([]);
  const { style, update } = usePosition(anchorRef, panelRef, { open: open && !asSheet, placement, offset: 6 });

  const close = useCallback((restoreFocus = true) => {
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  }, []);
  useDismiss({ open: open && !asSheet, onDismiss: () => close(false), refs: [anchorRef, panelRef], escape: false });

  const openMenu = () => {
    setAsSheet(
      typeof window !== 'undefined' &&
        typeof window.matchMedia === 'function' &&
        window.matchMedia(PHONE_QUERY).matches,
    );
    setOpen(true);
  };

  const enabled = () => itemEls.current.filter((el): el is HTMLButtonElement => !!el && !el.disabled);
  const focusAt = (index: number) => {
    const list = enabled();
    list[(index + list.length) % list.length]?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const list = enabled();
    const at = list.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === 'ArrowDown') focusAt(at + 1);
    else if (e.key === 'ArrowUp') focusAt(at - 1);
    else if (e.key === 'Home') focusAt(0);
    else if (e.key === 'End') focusAt(list.length - 1);
    else if (e.key === 'Escape') close(true);
    else if (e.key === 'Tab' && !asSheet) close(false);
    else return;
    if (e.key !== 'Tab') e.preventDefault();
  };

  const triggerProps: MenuTriggerProps = {
    ref: triggerRef,
    'aria-haspopup': 'menu',
    'aria-expanded': open,
    'aria-controls': open ? menuId : undefined,
    onClick: () => (open ? close(false) : openMenu()),
    onKeyDown: (e) => {
      if (e.key === 'ArrowDown' && !open) {
        e.preventDefault();
        openMenu();
      }
    },
  };

  const panel = (
    <MenuPanel
      id={menuId}
      items={items}
      onKeyDown={onKeyDown}
      className={asSheet ? 'min-w-0 p-0 shadow-none ring-0' : undefined}
      itemRef={(i, el) => {
        itemEls.current[i] = el;
        if (i === 0) firstItemRef.current = el;
      }}
      onSelect={(item) => {
        close(true);
        item.onSelect();
      }}
    />
  );

  return (
    <>
      <span ref={anchorRef} className="inline-flex">
        {trigger ? trigger(triggerProps) : <IconButton {...triggerProps} icon={icon} label={label} variant="ghost" />}
      </span>
      {open && asSheet ? (
        <Sheet open title={label} closeLabel={closeLabel} onClose={() => close(true)} initialFocus={firstItemRef}>
          {panel}
        </Sheet>
      ) : null}
      {open && !asSheet ? (
        <Portal>
          <div
            ref={(el) => {
              panelRef.current = el;
              if (el) {
                update();
                if (!el.contains(document.activeElement)) requestAnimationFrame(() => focusAt(0));
              }
            }}
            style={style}
            className="z-(--jc-z-dropdown)"
          >
            {panel}
          </div>
        </Portal>
      ) : null}
    </>
  );
}
